import { FC, useEffect, useMemo } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { LuPlus } from 'react-icons/lu';

import styles from './StanTipTapEditor.module.css';
import VariableItem from './CustomExtensions/variable-item';
import { cn } from '@/lib/utils/cn';
import { EditorActions } from './editor.logic';
import { DropdownProvider } from '../StanDropdown/DropdownProvider';
import { StanDropdownMenu } from '../StanDropdown/Menu/StanDropdownMenu';

export interface StanTipTapEditorCompactProps {
    value?: string;
    onChange?: (value: string) => void;
    className?: string;
    error?: boolean;
    disabled?: boolean;
    personalizeItemData?: { name: string; id: string; required: boolean }[];
    placeholder?: string;
}

/**
 * Compact version of StanTipTapEditor optimized for single-line inputs.
 * Features:
 * - Single-line editing (Enter key prevented)
 * - Paste sanitization (removes line breaks)
 * - External "Personalize" button
 * - Simplified bubble menu (Bold, Italic, Strike)
 * - No main toolbar
 */
export const StanTipTapEditorCompact: FC<StanTipTapEditorCompactProps> = ({
    value = '',
    onChange,
    className = '',
    error = false,
    disabled = false,
    personalizeItemData = [],
    placeholder = 'Start typing...',
}) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                link: false,
                hardBreak: false, // Disable hard breaks in compact mode
                paragraph: {
                    HTMLAttributes: {
                        class: 'compact-paragraph',
                    },
                },
            }),
            Placeholder.configure({
                placeholder,
            }),
            VariableItem,
        ],
        content: value,
        onUpdate: ({ editor }) => {
            onChange?.(editor.getHTML());
        },
        editorProps: {
            attributes: {
                class: 'compact',
            },
            handleKeyDown: (_, event) => {
                // Prevent Enter key from creating new lines
                if (event.key === 'Enter') {
                    event.preventDefault();
                    return true;
                }
                return false;
            },
            handlePaste: (view, event) => {
                // Sanitize pasted content for single-line formatting
                event.preventDefault();

                const html = event.clipboardData?.getData('text/html') || '';
                const plainText = event.clipboardData?.getData('text/plain') || '';

                let text = '';

                if (html) {
                    // Strip HTML tags to get plain text
                    const tempDiv = document.createElement('div');
                    tempDiv.innerHTML = html;
                    text = tempDiv.textContent || tempDiv.innerText || '';
                } else {
                    text = plainText;
                }

                // Remove all line breaks and normalize whitespace
                const sanitizedText = text
                    .replace(/\r?\n|\r/g, ' ')
                    .replace(/\s+/g, ' ')
                    .trim();

                if (sanitizedText) {
                    // Insert as plain text at current cursor position
                    const { state } = view;
                    const { tr } = state;
                    tr.insertText(sanitizedText);
                    view.dispatch(tr);
                }

                return true;
            },
        },
    });

    // Sync editor content when value prop changes externally
    useEffect(() => {
        if (editor && value !== undefined) {
            const currentContent = editor.getHTML();
            // Only update if the content is actually different to avoid unnecessary updates
            if (currentContent !== value) {
                editor.commands.setContent(value, { emitUpdate: false });
            }
        }
    }, [editor, value]);

    const actions = useMemo(() => new EditorActions(editor), [editor]);

    return (
        <>
            <div className={cn(styles.tipTapContainer, className, error && styles.error, disabled && styles.disabled)}>
                <EditorContent editor={editor} />
            </div>

            {/* External Personalize Button */}
            {personalizeItemData.length > 0 && (
                <DropdownProvider dropdownId='personalize-compact' dropdownName='personalize-compact'>
                    <StanDropdownMenu
                        id='personalize-compact'
                        name='personalize-compact'
                        options={personalizeItemData.map(item => ({
                            label: item.name,
                            value: item.id,
                        }))}
                        onSelect={(_, opt) => {
                            actions.insertVariableItem(opt.value, opt.label);
                        }}
                        placement='bottom-start'
                        className='w-fit'
                        disabled={disabled}
                    >
                        <button className={cn(styles.externalButton)} type='button' disabled={disabled}>
                            <LuPlus className='size-3' />
                            <span>Personalize</span>
                        </button>
                    </StanDropdownMenu>
                </DropdownProvider>
            )}
        </>
    );
};
